// contractPerformance — derives the 3-pillar values for the contract
// Details PerformanceGrade (deliverables / milestones / KPIs).
//
// Pillar derivations:
//   - Deliverables on-time = % submittedAt <= dueDate over the sample.
//     Recurring parent templates (recurrenceRule set, parentId absent) are
//     excluded first — they're scheduling scaffolding, not real work items.
//     Sample = submitted deliverables OR overdue-unsubmitted deliverables
//     (dueDate < today AEST, submittedAt null). Not-yet-due unsubmitted
//     items are excluded (awaiting data). The original submittedAt
//     is preserved across revision cycles, so a deliverable that submitted
//     on time then went through revisions still counts as on-time.
//     Submitted with no dueDate counts as on-time. Acceptance is captured
//     separately (acceptedAt) but does NOT feed this pillar.
//   - Milestones on-time = passed in directly from calculateOnTimeRate
//     so the meeting-style helper is the single source of truth.
//   - KPIs on-track = % of KPIs with at least one measurement whose
//     latest period reading is green RAG. KPIs with no measurements are
//     excluded from the sample (awaiting data, not failure).
//
// Threshold mapping (≥1 sample, inclusive on the higher tier).
// Defaults: success = 90, watch = 70. Per-contract overrides flow in
// via `performanceSuccessThreshold` / `performanceWatchThreshold` on
// ManagedContract; the watch < success constraint is enforced server
// side (Zod refinement) so callers can trust the values are
// well-ordered.
//
//   score >= success → success band (emerald)
//   score >= watch   → neutral band (amber — "watch")
//   score <  watch   → warning band (red — "at risk")
//
// Red on the warning band is intentional: the at-risk state must read
// as urgent at a glance.
//
// Synthesis denominator excludes muted pillars (sample = 0): the centre
// label only counts pillars that are actually reporting.

import type { OnTimeRateResult } from "../../components/manage/tracking/meeting/calculateOnTimeRate";
import { getBrisbaneToday } from "../../utils/getBrisbaneToday";

export interface DeliverableSummaryInput {
  id: string;
  dueDate: string | undefined;
  submittedAt: string | null;
  /** Set on recurring schedule parents (no parentId). Used to exclude them from
   *  the sample — they are occurrence templates, not real deliverables. */
  recurrenceRule?: unknown;
  parentId?: string;
}

export interface KpiSummaryInput {
  id: string;
  latestRag: "green" | "amber" | "red" | null;
}

export interface ContractPerformanceSnapshot {
  deliverables: DeliverableSummaryInput[];
  milestonesOnTimeRate: OnTimeRateResult;
  kpis: KpiSummaryInput[];
}

export type PillarThreshold =
  | "success"
  | "neutral"
  | "warning"
  | "insufficient";

export interface PillarResult {
  onTimePct: number | null;
  onTrackPct?: number | null;
  sampleSize: number;
  threshold: PillarThreshold;
  smallSample: boolean;
}

export type OverallLabel = "Healthy" | "Watch" | "At risk" | "Insufficient data";

export interface ContractPerformanceResult {
  deliverables: PillarResult;
  milestones: PillarResult;
  kpis: PillarResult;
  overallLabel: OverallLabel;
  reportingPillarCount: number;
  pillarsTrackingInSuccess: number;
}

const SMALL_SAMPLE_THRESHOLD = 3;

export const DEFAULT_SUCCESS_THRESHOLD = 90;
export const DEFAULT_WATCH_THRESHOLD = 70;

export interface PerformanceThresholdConfig {
  successThreshold?: number | null;
  watchThreshold?: number | null;
}

function resolveThresholds(
  config?: PerformanceThresholdConfig,
): { success: number; watch: number } {
  const success = config?.successThreshold ?? DEFAULT_SUCCESS_THRESHOLD;
  const watch = config?.watchThreshold ?? DEFAULT_WATCH_THRESHOLD;
  return { success, watch };
}

function thresholdFor(
  pct: number | null,
  bands: { success: number; watch: number },
): PillarThreshold {
  if (pct === null) return "insufficient";
  if (pct >= bands.success) return "success";
  if (pct >= bands.watch) return "neutral";
  return "warning";
}

function pillarFor(
  pct: number | null,
  sampleSize: number,
  bands: { success: number; watch: number },
): PillarResult {
  return {
    onTimePct: pct,
    sampleSize,
    threshold: thresholdFor(pct, bands),
    smallSample: sampleSize > 0 && sampleSize < SMALL_SAMPLE_THRESHOLD,
  };
}

function deliverablesPillar(
  deliverables: DeliverableSummaryInput[],
  bands: { success: number; watch: number },
  today: string,
): PillarResult {
  // Recurring parent templates (recurrenceRule set, no parentId) are excluded —
  // they're occurrence-generation scaffolding, not real work items. Mirrors the
  // equivalent filter in backend/src/services/contractGradeService.ts.
  const candidates = deliverables.filter((d) => !(d.recurrenceRule && !d.parentId));
  // Sample = submitted OR overdue-unsubmitted (dueDate strictly < today, not yet submitted).
  // Not-yet-due unsubmitted items are awaiting data and stay out of the sample.
  const sample = candidates.filter(
    (d) =>
      d.submittedAt !== null ||
      (d.dueDate !== undefined && d.dueDate < today),
  );
  const sampleSize = sample.length;
  if (sampleSize === 0) {
    return pillarFor(null, 0, bands);
  }
  let onTime = 0;
  for (const d of sample) {
    if (d.submittedAt === null) {
      // Overdue-unsubmitted: counts as failure (0 contribution).
      continue;
    }
    if (!d.dueDate) {
      // Submitted with no dueDate: counts as on-time.
      onTime += 1;
      continue;
    }
    // submittedAt is an ISO timestamp; dueDate is YYYY-MM-DD. Compare
    // by extracting the YYYY-MM-DD portion of submittedAt.
    const submittedDate = (d.submittedAt as string).slice(0, 10);
    if (submittedDate <= d.dueDate) onTime += 1;
  }
  const pct = Math.round((onTime / sampleSize) * 100);
  return pillarFor(pct, sampleSize, bands);
}

function milestonesPillar(
  rate: OnTimeRateResult,
  bands: { success: number; watch: number },
): PillarResult {
  return pillarFor(rate.pct, rate.sampleSize, bands);
}

function kpisPillar(
  kpis: KpiSummaryInput[],
  bands: { success: number; watch: number },
): PillarResult {
  // Only KPIs with at least one measurement (latestRag !== null) enter the
  // sample. Unmeasured KPIs are awaiting data, not failure.
  const measured = kpis.filter((k) => k.latestRag !== null);
  const sampleSize = measured.length;
  if (sampleSize === 0) {
    return {
      onTimePct: null,
      onTrackPct: null,
      sampleSize: 0,
      threshold: "insufficient",
      smallSample: false,
    };
  }
  const onTrack = measured.filter((k) => k.latestRag === "green").length;
  const pct = Math.round((onTrack / sampleSize) * 100);
  return {
    ...pillarFor(pct, sampleSize, bands),
    onTrackPct: pct,
  };
}

function synthesise(
  pillars: PillarResult[],
): { overallLabel: OverallLabel; reportingPillarCount: number; pillarsTrackingInSuccess: number } {
  const reporting = pillars.filter((p) => p.threshold !== "insufficient");
  const reportingPillarCount = reporting.length;
  if (reportingPillarCount === 0) {
    return {
      overallLabel: "Insufficient data",
      reportingPillarCount: 0,
      pillarsTrackingInSuccess: 0,
    };
  }
  const pillarsTrackingInSuccess = reporting.filter(
    (p) => p.threshold === "success",
  ).length;
  if (pillarsTrackingInSuccess === reportingPillarCount) {
    return {
      overallLabel: "Healthy",
      reportingPillarCount,
      pillarsTrackingInSuccess,
    };
  }
  if (pillarsTrackingInSuccess === 0) {
    return {
      overallLabel: "At risk",
      reportingPillarCount,
      pillarsTrackingInSuccess,
    };
  }
  return {
    overallLabel: "Watch",
    reportingPillarCount,
    pillarsTrackingInSuccess,
  };
}

export function computeContractPerformance(
  snap: ContractPerformanceSnapshot,
  thresholds?: PerformanceThresholdConfig,
  today: string = getBrisbaneToday(),
): ContractPerformanceResult {
  const bands = resolveThresholds(thresholds);
  const deliverables = deliverablesPillar(snap.deliverables, bands, today);
  const milestones = milestonesPillar(snap.milestonesOnTimeRate, bands);
  const kpis = kpisPillar(snap.kpis, bands);
  const synthesis = synthesise([deliverables, milestones, kpis]);

  return {
    deliverables,
    milestones,
    kpis,
    ...synthesis,
  };
}
